// frontend/src/components/navigation/UpdatePassword.jsx
import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { apiPut } from "../../utils/api";
import { getRole, clearRequiresPasswordReset } from "../../utils/authService";
import { toast } from "../../utils/toast";
import { Eye, EyeOff, ShieldAlert } from "lucide-react";
import logo from "../../assets/gca.png";
import "./login.css";

function UpdatePassword() {
  const navigate = useNavigate();
  const location = useLocation();
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [updating, setUpdating] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (newPassword !== confirmPassword) {
      return setError("New passwords do not match");
    }
    if (newPassword.length < 8) {
      return setError("Password must be at least 8 characters");
    }

    setUpdating(true);
    try {
      const res = await apiPut("/api/auth/change-password", { oldPassword, newPassword });
      const data = await res.json().catch(() => ({}));

      if (res.ok) {
        clearRequiresPasswordReset();
        toast.success("Password updated. You're all set.");
        const role = getRole();
        const returnTo = location.state?.returnTo;
        if (returnTo) {
          navigate(returnTo, { replace: true });
        } else if (role === "admin") {
          navigate("/admin/dashboard", { replace: true });
        } else {
          navigate("/devices", { replace: true });
        }
      } else {
        setError(data.message || "Failed to update password");
      }
    } catch {
      setError("Server error. Please try again later.");
    } finally {
      setUpdating(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-card">
        <img src={logo} alt="GCA Logo" className="login-logo" />

        <h2 className="login-title">Update Your Password</h2>
        <p className="login-subtitle">
          <ShieldAlert size={15} style={{ verticalAlign: "-2px", marginRight: 4 }} />
          Your current password doesn't meet our security policy. Please set a stronger one to continue.
        </p>

        {error && (
          <div className="login-maint-alert login-maint-alert--active">
            <span>{error}</span>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Current Password</label>
            <input
              type="password"
              value={oldPassword}
              required
              onChange={(e) => setOldPassword(e.target.value)}
            />
          </div>

          <div className="form-group">
            <label>New Password</label>
            <div className="password-input-wrapper">
              <input
                type={showPassword ? "text" : "password"}
                value={newPassword}
                required
                onChange={(e) => setNewPassword(e.target.value)}
              />
              <button
                type="button"
                className="password-toggle-btn"
                onClick={() => setShowPassword(!showPassword)}
                tabIndex={-1}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            <small style={{ color: "var(--text-muted)" }}>
              At least 8 characters with a mix of uppercase, lowercase, numbers, and symbols.
            </small>
          </div>

          <div className="form-group">
            <label>Confirm New Password</label>
            <input
              type={showPassword ? "text" : "password"}
              value={confirmPassword}
              required
              onChange={(e) => setConfirmPassword(e.target.value)}
            />
          </div>

          <button type="submit" className="login-btn" disabled={updating}>
            {updating ? "Updating..." : "Update Password"}
          </button>
        </form>
      </div>
      <div className="login-footer">© {new Date().getFullYear()} Global Cyber Associates</div>
    </div>
  );
}

export default UpdatePassword;
